import type { HubTierClass, ScoreDetailsV4, ScoreFileDetail } from '../../types';

/** Overall track is fixed 0–10, same axis the "Overall" row and the score badge use. */
export const BREAKDOWN_MAX = 10;

export type BreakdownRowId = 'base' | 'credit' | 'penalty' | 'bonus';

export interface BreakdownRow {
  id: BreakdownRowId;
  label: string;
  /** Cumulative position before/after this row, 0–10. For the bonus row this is visually
   *  clipped at 10 even when the true amount would run past it (scores are capped at 10). */
  from: number;
  to: number;
  /** True, unclipped contribution — the number shown next to the bar. */
  amount: number;
  empty: boolean;
  /** Short phrase shown in place of a bar when this row adds or takes nothing. */
  emptyNote?: string;
}

export interface ScoreBreakdown {
  rows: BreakdownRow[];
  base: number;
  skillCredit: number;
  penaltyTotal: number;
  ruleBonus: number;
  agentBonus: number;
  bonusTotal: number;
  /** base + credit + bonus − penalties, before the 0–10 clamp. */
  rawTotal: number;
  /** rawTotal clamped to 0–10 — what the overall badge should show. */
  finalScore: number;
  clamped: boolean;
}

/** Short stand-in for an empty waterfall row. Never the word "None". */
export function waterfallEmptyPhrase(id: 'credit' | 'penalty' | 'bonus', attached: boolean): string {
  if (id === 'credit') {
    return attached ? 'No credit from attached skills' : 'Not applicable — no skill attached';
  }
  if (id === 'penalty') {
    return 'No penalties on this prompt';
  }
  return attached ? 'No bonus from what was attached' : 'Not applicable — none attached';
}

/** Walks Base → Skill credit → Penalties → Supporting bonus, same order as the design's waterfall. */
export function buildScoreBreakdown(v4: ScoreDetailsV4): ScoreBreakdown {
  const d = v4.derived ?? {};
  const files = d.file_details ?? [];
  const skillsAttached = files.some((file) => file.kind === 'skill');
  const supportAttached = files.some((file) => file.kind === 'rule' || file.kind === 'agent');
  const base = Number(d.base_score ?? 0) || 0;
  const skillCredit = Number(d.skill_score ?? 0) || 0;
  const ruleBonus = Number(d.rule_bonus ?? 0) || 0;
  const agentBonus = Number(d.agent_bonus ?? 0) || 0;
  const bonusTotal = ruleBonus + agentBonus;
  const penaltyTotal = (d.penalties ?? []).reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const rows: BreakdownRow[] = [];
  let cursor = base;
  rows.push({ id: 'base', label: 'Base score', from: 0, to: base, amount: base, empty: false });

  if (skillCredit > 0) {
    rows.push({ id: 'credit', label: 'Skill credit', from: cursor, to: cursor + skillCredit, amount: skillCredit, empty: false });
    cursor += skillCredit;
  } else {
    rows.push({
      id: 'credit',
      label: 'Skill credit',
      from: cursor,
      to: cursor,
      amount: 0,
      empty: true,
      emptyNote: waterfallEmptyPhrase('credit', skillsAttached),
    });
  }

  if (penaltyTotal > 0) {
    const next = Math.max(0, cursor - penaltyTotal);
    rows.push({ id: 'penalty', label: 'Penalties', from: next, to: cursor, amount: -penaltyTotal, empty: false });
    cursor = next;
  } else {
    rows.push({
      id: 'penalty',
      label: 'Penalties',
      from: cursor,
      to: cursor,
      amount: 0,
      empty: true,
      emptyNote: waterfallEmptyPhrase('penalty', false),
    });
  }

  if (bonusTotal > 0) {
    const visualTo = Math.min(BREAKDOWN_MAX, cursor + bonusTotal);
    rows.push({ id: 'bonus', label: 'Supporting bonus', from: cursor, to: visualTo, amount: bonusTotal, empty: false });
    cursor += bonusTotal;
  } else {
    rows.push({
      id: 'bonus',
      label: 'Supporting bonus',
      from: cursor,
      to: cursor,
      amount: 0,
      empty: true,
      emptyNote: waterfallEmptyPhrase('bonus', supportAttached),
    });
  }

  const rawTotal = base + skillCredit + bonusTotal - penaltyTotal;
  const finalScore = Math.max(0, Math.min(BREAKDOWN_MAX, rawTotal));

  return {
    rows,
    base,
    skillCredit,
    penaltyTotal,
    ruleBonus,
    agentBonus,
    bonusTotal,
    rawTotal,
    finalScore,
    clamped: rawTotal > BREAKDOWN_MAX,
  };
}

/** Trims trailing zeros so weights read "1" / "0.5" / "0.25" instead of "1.00". */
export function trimNum(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value.toFixed(2).replace(/\.?0+$/, '') || '0';
}

/** Waterfall row values keep exactly one decimal unless a second one is significant: 7.20 → "7.2", 0.75 stays. */
export function formatFallValue(value: number): string {
  if (!Number.isFinite(value)) return '0.0';
  const fixed = Math.abs(value).toFixed(2);
  return fixed.endsWith('0') ? fixed.slice(0, -1) : fixed;
}

/** The overall badge/number: whole scores read "10", fractional scores keep one decimal. */
export function formatOverall(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function breakdownCaption(b: ScoreBreakdown): string {
  const total = trimNum(b.finalScore);
  if (b.bonusTotal > 0 && b.penaltyTotal > 0) {
    return `Bonuses add ${trimNum(b.bonusTotal)} and penalties take ${trimNum(b.penaltyTotal)}. Scores are capped at 10.`;
  }
  if (b.bonusTotal > 0) {
    return `The bonuses bring the total to ${total}. Scores are capped at 10.`;
  }
  if (b.penaltyTotal > 0) {
    return `The penalties bring the total down to ${total}. Scores are capped at 10.`;
  }
  return 'Scores are capped at 10.';
}

const ELIGIBLE_TIERS: HubTierClass[] = ['org', 'domain', 'experimental'];
const TIER_WEIGHT: Record<string, number> = { org: 1, domain: 0.5, experimental: 0.25 };
const TIER_SHORT: Record<string, string> = { org: 'Org', domain: 'Domain', experimental: 'Experimental' };
const RELEVANCE_WEIGHT: Record<string, number> = { core: 1, supporting: 0.5, irrelevant: 0 };
const RELEVANCE_SHORT: Record<string, string> = { core: 'Core', supporting: 'Supporting', irrelevant: 'Irrelevant' };

export function eligibleSkills(skills: ScoreFileDetail[]): ScoreFileDetail[] {
  return skills.filter((f) => ELIGIBLE_TIERS.includes(f.tier_class));
}

export interface CountChip {
  key: string;
  label: string;
  count: number;
  pillClass: string;
}

/** Count chips by Hub tier across every attached skill, zero counts omitted. */
export function tierCountChips(skills: ScoreFileDetail[]): CountChip[] {
  const order: HubTierClass[] = ['org', 'domain', 'experimental', 'unidentified', 'rejected'];
  const labels: Record<HubTierClass, string> = {
    org: 'Org',
    domain: 'Domain',
    experimental: 'Experimental',
    unidentified: 'Unidentified',
    rejected: 'Rejected',
  };
  const counts: Record<string, number> = {};
  skills.forEach((f) => {
    counts[f.tier_class] = (counts[f.tier_class] || 0) + 1;
  });
  return order
    .filter((tier) => counts[tier])
    .map((tier) => ({ key: tier, label: labels[tier], count: counts[tier], pillClass: `pill--${tier}` }));
}

/** Count chips by relevance label, only skills the Hub identified carry a label. */
export function relevanceCountChips(skills: ScoreFileDetail[]): CountChip[] {
  const order = ['core', 'supporting', 'irrelevant'];
  const counts: Record<string, number> = {};
  skills.forEach((f) => {
    const label = f.relevance_label?.trim().toLowerCase();
    if (label) counts[label] = (counts[label] || 0) + 1;
  });
  return order
    .filter((label) => counts[label])
    .map((label) => ({
      key: label,
      label: RELEVANCE_SHORT[label] ?? label,
      count: counts[label],
      pillClass: `pill--${label}`,
    }));
}

/** "Is your skill approved in HUB?" explainer — scales from a direct statement to an averaged one. */
export function typeScoreExplainer(skills: ScoreFileDetail[]): string {
  const eligible = eligibleSkills(skills);
  const lead = 'Is your skill approved in HUB?';
  if (eligible.length === 0) return `${lead} No Hub-identified skill in this prompt earns a type score.`;
  if (eligible.length === 1) {
    const tier = eligible[0].tier_class;
    return `${lead} ${TIER_SHORT[tier]} counts ${trimNum(TIER_WEIGHT[tier])}.`;
  }
  const legend = ELIGIBLE_TIERS.map((t) => `${TIER_SHORT[t]} ${trimNum(TIER_WEIGHT[t])}`).join(', ');
  const weights = eligible.map((f) => trimNum(TIER_WEIGHT[f.tier_class])).join(' + ');
  return `${lead} ${legend}. Average over the skills HUB identified: (${weights}) ÷ ${eligible.length}.`;
}

/** "Does the skill fit this task?" explainer — only Hub-identified (eligible) skills count. */
export function relevanceScoreExplainer(skills: ScoreFileDetail[]): string {
  const eligible = eligibleSkills(skills).filter((f) => f.relevance_label);
  const lead = 'Does the skill fit this task? Only skills identified in HUB are counted.';
  if (eligible.length === 0) return `${lead} No Hub-identified skill in this prompt earns a relevance score.`;
  if (eligible.length === 1) {
    const label = eligible[0].relevance_label!.trim().toLowerCase();
    return `${lead} ${RELEVANCE_SHORT[label] ?? label} counts ${trimNum(RELEVANCE_WEIGHT[label] ?? 0)}.`;
  }
  const legend = ['core', 'supporting', 'irrelevant'].map((l) => `${RELEVANCE_SHORT[l]} ${trimNum(RELEVANCE_WEIGHT[l])}`).join(', ');
  const weights = eligible.map((f) => trimNum(RELEVANCE_WEIGHT[f.relevance_label!.trim().toLowerCase()] ?? 0)).join(' + ');
  return `${lead} ${legend}: (${weights}) ÷ ${eligible.length}.`;
}

export interface RuleAgentRow {
  id: 'rule' | 'agent';
  label: string;
  amount: number;
}

/** Rules and agents never show a file name on screen — just which role earned the bonus. */
export function rulesAndAgentsRows(v4: ScoreDetailsV4): RuleAgentRow[] {
  const d = v4.derived ?? {};
  const rows: RuleAgentRow[] = [];
  if (Number(d.rule_bonus ?? 0) > 0) rows.push({ id: 'rule', label: 'Supporting Rule', amount: Number(d.rule_bonus) });
  if (Number(d.agent_bonus ?? 0) > 0) rows.push({ id: 'agent', label: 'Supporting Agent', amount: Number(d.agent_bonus) });
  return rows;
}

/** True once any rule/agent file was attached, even if it earned no bonus (e.g. rejected or a repeat). */
export function hasAnyRuleOrAgentAttachment(v4: ScoreDetailsV4): boolean {
  return (v4.derived?.file_details ?? []).some((f) => f.kind === 'rule' || f.kind === 'agent');
}
